import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import theme from '../theme';
import {
	Box,
	HStack,
	VStack,
	Button,
	Separator,
	Heading,
	Spacer,
	Text,
	Flex,
	Switch,
	Textarea,
	Input,
	Accordion,
	Field,
	Portal,
	Dialog,
	CloseButton
} from '@chakra-ui/react';
import {
	hydrateObservationForm,
	updateActivity,
	updateTypeOfObservation,
	updateCondition,
	updateTextField,
	updateHazardStatus,
	updateNearMisses,
	updateDate
} from '../store/safetyDatabaseObservation/safetyDatabaseObservation.actions';
import SafetyObservationAttachments from './SafetyObservationAttachments';
import Autocomplete from '../components/Autocomplete';
import DatePicker from 'react-datepicker';
import { saveSafetyObservation } from '../lib/safetyForms';
import { FaArrowLeft } from 'react-icons/fa';

import 'react-datepicker/dist/react-datepicker.css';

const TextField = ({ title, requirement = false, value, update, isDisabled }) => (
	<Flex flexDirection="row" alignItems="center" justifyContent="space-between" wrap="wrap">
		<Box w="100%" p="8px" m="8px" mx={[0, 0, '8px']} bgColor={theme.colors.gray[500]} borderRadius="base">
			<Text fontSize={16} as="label" pb="4px">
				{title}
			</Text>
			<Field.Root invalid={requirement}>
				<Textarea
					fontSize={16}
					disabled={isDisabled}
					_disabled={{
						opacity: 1,
						bgColor: theme.colors.gray[400],
						color: theme.colors.black
					}}
					bgColor={theme.colors.white}
					value={value}
					onChange={({ target }) => update(target.value)}
				/>
			</Field.Root>
		</Box>
	</Flex>
);

export default ({ submission, handleExport }) => {
	const dispatch = useDispatch();

	const [attachments, setAttachments] = React.useState(false);
	const [removeAutocompleteDialogOpen, setRemoveAutocompleteDialogOpen] = React.useState(false);
	const [anonymous, setAnonymous] = React.useState(!submission.observer_name);
	const [editMode, setEditMode] = React.useState(false);
	const [confirmChanges, setConfirmChanges] = React.useState(false);
	const [errorDialog, setErrorDialog] = React.useState(null);

	const confirmChangesRef = React.useRef();
	const errorModalRef = React.useRef();

	const {
		activity,
		type_of_observation: typeOfObservation,
		conditions,
		what,
		why,
		on_site_solution: onsiteSolution,
		hazard_status: hazardStatus,
		hazard_status_comment: hazardStatusComment,
		near_misses: nearMisses,
		near_miss_description: nearMissDescription,
		project_location: projectLocation,
		observer_name: observerName,
		date,
		photos
	} = useSelector(state => state.safetyDatabaseObservation);

	React.useEffect(() => {
		if (submission) {
			dispatch(hydrateObservationForm({ submission }));
		} else if (!date.manual) {
			dispatch(
				updateDate({
					date: Date.now(),
					manual: date.manual
				})
			);
		}
	}, []);

	const projectLocationList = React.useMemo(
		() =>
			window.projectList.map(project => ({
				project: `${project.phase_name} - ${project.project_name}: ${project.project_number}-${project.phase_number}`
			})),
		[window.projectList]
	);

	const employeeCrewMembers = React.useMemo(() => {
		const map = new Map();
		window.employeeList.forEach(employee => map.set(employee.name, employee));
		return map;
	}, []);

	const activityReqMet = React.useMemo(() => Object.values(activity).some(activity => activity.value), [activity]);

	const typeOfObservationReqMet = React.useMemo(
		() => Object.values(typeOfObservation).some(observation => observation.value),
		[typeOfObservation]
	);

	const nearMissDescriptionReqMet = React.useMemo(() => {
		if (Object.values(nearMisses).some(nearMiss => nearMiss.value)) {
			return !!nearMissDescription;
		}
		return true;
	}, [nearMisses, nearMissDescription]);

	return attachments ? (
		<SafetyObservationAttachments
			readOnly={submission}
			attachmentsList={photos}
			close={() => setAttachments(false)}
		/>
	) : (
		<Box px={4} pt={4} pb={100}>
			<Dialog.Root
				role="alertdialog"
				initialFocusEl={confirmChangesRef}
				onOpenChange={({ open }) => {
					if (!open) {
						setConfirmChanges(false);
					}
				}}
				open={confirmChanges}
				placement="center"
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content opacity="1 !important">
							<Dialog.Header>
								<Dialog.Title fontSize="lg" fontWeight="bold">
									Confirm Changes
								</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<p>Are you sure you want to save your changes?</p>
							</Dialog.Body>
							<Dialog.Footer>
								<Dialog.ActionTrigger asChild>
									<Button
										ref={confirmChangesRef}
										color={theme.colors.white}
										bg={theme.colors.red[500]}
										_hover={{ background: theme.colors.red[600] }}
										onClick={() => {
											setConfirmChanges(false);
											setEditMode(false);
											setAnonymous(!submission.observer_name);

											dispatch(hydrateObservationForm({ submission }));
										}}
									>
										No
									</Button>
								</Dialog.ActionTrigger>
								<Button
									colorPalette="red"
									ml={3}
									color={theme.colors.white}
									bg={theme.colors.blue[700]}
									_hover={{ backgroundColor: theme.colors.blue[800] }}
									onClick={async () => {
										setConfirmChanges(false);

										await saveSafetyObservation(submission.sid, {
											activity,
											type_of_observation: typeOfObservation,
											conditions,
											what,
											why,
											on_site_solution: onsiteSolution,
											hazard_status: hazardStatus,
											hazard_status_comment: hazardStatusComment,
											near_misses: nearMisses,
											near_miss_description: nearMissDescription,
											project_location: projectLocation,
											observer_name: !anonymous ? observerName : '',
											date: moment(date.date).format('YYYY-MM-DD'),
											photos
										})
											.then(() => setEditMode(false))
											.catch(error => {
												setErrorDialog({
													header: 'Error Saving Changes',
													body: error.message
												});
											});
									}}
								>
									Yes
								</Button>
							</Dialog.Footer>
							<Dialog.CloseTrigger asChild>
								<CloseButton size="sm" />
							</Dialog.CloseTrigger>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
			<Dialog.Root
				role="alertdialog"
				initialFocusEl={errorModalRef}
				onOpenChange={({ open }) => {
					if (!open) {
						setErrorDialog(null);
					}
				}}
				open={Boolean(errorDialog)}
				placement="center"
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content opacity="1 !important">
							<Dialog.Header>
								<Dialog.Title fontSize="lg" fontWeight="bold">
									{errorDialog?.header}
								</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<p>{errorDialog?.body}</p>
							</Dialog.Body>
							<Dialog.Footer>
								<Dialog.ActionTrigger asChild>
									<Button
										ref={errorModalRef}
										ml={3}
										color={theme.colors.white}
										bg={theme.colors.blue[700]}
										_hover={{ backgroundColor: theme.colors.blue[800] }}
										onClick={() => setErrorDialog(null)}
									>
										Okay
									</Button>
								</Dialog.ActionTrigger>
							</Dialog.Footer>
							<Dialog.CloseTrigger asChild>
								<CloseButton size="sm" />
							</Dialog.CloseTrigger>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
			<HStack
				spacing={2}
				justify={['flex-start', 'flex-start', 'flex-end']}
				p={2}
				flexWrap={['wrap', 'wrap', 'nowrap']}
			>
				<Button
					size="lg"
					color={theme.colors.white}
					bg={theme.colors.blue[700]}
					_hover={{ backgroundColor: theme.colors.blue[800] }}
					onClick={() => window.open(window.location.origin + '/dashboard/safety-database/', '_self')}
					borderRadius="4px"
					p="5px 5px 5px 8px"
				>
					<FaArrowLeft w="30px" h="30px" />
				</Button>
				<VStack spacing="8px" mb="24px !important" align="flex-start" w={['100%', '100%', 'auto']}>
					<Heading size="lg" pt="24px" color={theme.colors.blue[700]}>
						Safety Observation Form
					</Heading>
				</VStack>
				<Spacer display={['none', 'none', 'block']} />
				<Button
					mb={['12px !important', '12px !important', '0']}
					size="lg"
					color={theme.colors.white}
					bg={theme.colors.blue[700]}
					_hover={{ backgroundColor: theme.colors.blue[800] }}
					onClick={() => handleExport([submission.sid])}
				>
					Export
				</Button>
				<Button
					mb={['12px !important', '12px !important', '0']}
					size="lg"
					color={theme.colors.white}
					bg={theme.colors.blue[700]}
					_hover={{ backgroundColor: theme.colors.blue[800] }}
					onClick={() => setAttachments(true)}
				>
					Form Attachments
				</Button>
				{submission && window.userRoles.includes('administrator') && (
					<Button
						mb={['12px !important', '12px !important', '0']}
						size="lg"
						color={editMode ? theme.colors.black : theme.colors.white}
						bg={editMode ? 'gray.100' : theme.colors.blue[700]}
						_hover={{ backgroundColor: editMode ? 'gray.200' : theme.colors.blue[800] }}
						variant={editMode ? 'ghost' : 'solid'}
						onClick={() => {
							if (editMode) {
								setConfirmChanges(true);
							} else {
								setEditMode(editMode => !editMode);
							}
						}}
					>
						Edit Form
					</Button>
				)}
			</HStack>
			<Separator />
			<Box maxW="100%" p="8px" m="8px" mx={[0, 0, '8px']} bgColor={theme.colors.gray[500]} borderRadius="base">
				<Text fontSize={20} lineHeight={1.25}>
					Your concerns for safety and suggestions as how to improve our safety program are important. Use
					this form to submit recognition for safe behaviors you observe or anything that causes you to be
					concerned about the potential for injury. Describe your observations and concerns in the appropriate
					space. Listing your name is optional but please do not list the name of the person(s) being
					observed. We will use this information to continually improve our safety practices and culture.
				</Text>
			</Box>
			<Accordion.Root multiple collapsible p="8px" defaultValue={[0, 1, 2, 3]}>
				<Accordion.Item value={0} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Activity
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex
								flexDirection="row"
								alignItems="stretch"
								justifyContent="space-between"
								flexWrap={['wrap', 'wrap', 'nowrap']}
							>
								{Object.keys(activity).map((key, index) => (
									<HStack
										key={`${key}_${index}`}
										w="100%"
										p="8px"
										m="8px"
										mx={[0, 0, '8px']}
										bgColor={theme.colors.gray[500]}
										borderRadius="base"
										borderBottom={`2px solid ${!activityReqMet ? 'red' : 'transparent'}`}
									>
										<Switch.Root
											colorPalette="blue"
											size="lg"
											checked={activity[key].value}
											onCheckedChange={() => dispatch(updateActivity(key, !activity[key].value))}
											pointerEvents={submission ? 'none' : 'auto'}
											disabled={Boolean(submission)}
											_hover={{ cursor: submission ? 'default' : 'pointer' }}
										>
											<Switch.HiddenInput />
											<Switch.Control />
										</Switch.Root>
										<Text fontSize={16} as="label" pl="4px">
											{activity[key].title}
										</Text>
									</HStack>
								))}
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={1} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Type Of Observation
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex
								flexDirection="row"
								alignItems="stretch"
								justifyContent="space-between"
								flexWrap={['wrap', 'wrap', 'nowrap']}
							>
								{Object.keys(typeOfObservation).map((key, index) => (
									<HStack
										key={`${key}_${index}`}
										w="100%"
										p="8px"
										m="8px"
										mx={[0, 0, '8px']}
										bgColor={theme.colors.gray[500]}
										borderRadius="base"
										borderBottom={`2px solid ${!typeOfObservationReqMet ? 'red' : 'transparent'}`}
									>
										<Switch.Root
											colorPalette="blue"
											size="lg"
											checked={typeOfObservation[key].value}
											onCheckedChange={() =>
												dispatch(updateTypeOfObservation(key, !typeOfObservation[key].value))
											}
											disabled={Boolean(submission)}
											pointerEvents={submission ? 'none' : 'auto'}
											_hover={{ cursor: submission ? 'default' : 'pointer' }}
										>
											<Switch.HiddenInput />
											<Switch.Control />
										</Switch.Root>
										<Text fontSize={16} as="label" pl="4px">
											{typeOfObservation[key].title}
										</Text>
									</HStack>
								))}
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={2} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Check The Conditions That Apply:
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex flexDirection="row" alignItems="center" justifyContent="space-between" wrap="wrap">
								{Object.keys(conditions).map((conditionSection, index) => (
									<Box
										key={`${conditionSection}_${index}`}
										p="8px"
										my="8px"
										mr={[0, 0, index % 2 === 0 ? '4px' : '8px']}
										ml={[0, 0, index % 2 === 1 ? '4px' : '8px']}
										bgColor={theme.colors.gray[500]}
										borderRadius="base"
										minW={['100%', '100%', '48%']}
									>
										<Text fontSize={16} as="label" pb="4px">
											{conditions[conditionSection].title}
										</Text>
										<Flex flexDirection="row" pb="8px">
											<Box flex="2" />
											<Box flex="1">
												<Text fontSize={16} textAlign="center">
													Safe
												</Text>
											</Box>
											<Box flex="1">
												<Text fontSize={16} textAlign="center">
													Concern
												</Text>
											</Box>
										</Flex>
										{Object.keys(conditions[conditionSection].fields).map(
											(condition, conditionIndex) => (
												<Flex
													key={`${condition}_${conditionIndex}`}
													flexDirection="row"
													alignItems="center"
													justifyContent="space-between"
													wrap="wrap"
													pb="8px"
												>
													<Flex flexDirection="row" alignItems="center" w="100%">
														<Box flex="2">
															<Text fontSize={16}>
																{conditions[conditionSection].fields[condition].title}
															</Text>
														</Box>
														<Flex flex="1" justifyContent="center">
															<Switch.Root
																colorPalette="blue"
																size="lg"
																checked={
																	conditions[conditionSection].fields[condition].safe
																}
																onCheckedChange={() =>
																	dispatch(
																		updateCondition({
																			section: conditionSection,
																			condition,
																			safe: !conditions[conditionSection].fields[
																				condition
																			].safe,
																			concern: false
																		})
																	)
																}
																disabled={Boolean(submission)}
																pointerEvents={submission ? 'none' : 'auto'}
																_hover={{ cursor: submission ? 'default' : 'pointer' }}
															>
																<Switch.HiddenInput />
																<Switch.Control />
															</Switch.Root>
														</Flex>
														<Flex flex="1" justifyContent="center">
															<Switch.Root
																size="lg"
																checked={
																	conditions[conditionSection].fields[condition]
																		.concern
																}
																onCheckedChange={() =>
																	dispatch(
																		updateCondition({
																			section: conditionSection,
																			condition,
																			safe: false,
																			concern:
																				!conditions[conditionSection].fields[
																					condition
																				].concern
																		})
																	)
																}
																colorPalette="red"
																disabled={Boolean(submission)}
																pointerEvents={submission ? 'none' : 'auto'}
																_hover={{ cursor: submission ? 'default' : 'pointer' }}
															>
																<Switch.HiddenInput />
																<Switch.Control />
															</Switch.Root>
														</Flex>
													</Flex>
												</Flex>
											)
										)}
									</Box>
								))}
							</Flex>
							<TextField
								disabled={Boolean(submission)}
								title="What: (Behavior Observed/Incident Description)"
								requirement={!what}
								value={what}
								update={value => dispatch(updateTextField({ key: 'what', value }))}
							/>
							<TextField
								disabled={Boolean(submission)}
								title="Why: (Feedback From Observer On Cause)"
								requirement={!why}
								value={why}
								update={value => dispatch(updateTextField({ key: 'why', value }))}
							/>
							<TextField
								disabled={Boolean(submission)}
								title="On-Site Solution:"
								requirement={!onsiteSolution}
								value={onsiteSolution}
								update={value => dispatch(updateTextField({ key: 'on_site_solution', value }))}
							/>
							<Heading
								fontSize={24}
								as="label"
								pt="24px"
								pb="12px"
								color={theme.colors.blue[700]}
								px="8px"
							>
								Hazard Status:
							</Heading>
							<Flex flexDirection="row" alignItems="center" justifyContent="space-between">
								<HStack
									w="100%"
									p="8px"
									m="8px"
									ml={[0, 0, '8px']}
									bgColor={theme.colors.gray[500]}
									borderRadius="base"
									borderBottom={`2px solid ${
										!(hazardStatus.completed || hazardStatus.pending) ? 'red' : 'transparent'
									}`}
								>
									<Switch.Root
										colorPalette="blue"
										size="lg"
										checked={hazardStatus.completed}
										onCheckedChange={() =>
											dispatch(
												updateHazardStatus({
													completed: !hazardStatus.completed,
													pending: false
												})
											)
										}
										disabled={Boolean(submission)}
										pointerEvents={submission ? 'none' : 'auto'}
										_hover={{ cursor: submission ? 'default' : 'pointer' }}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Completed
									</Text>
								</HStack>
								<HStack
									w="100%"
									p="8px"
									m="8px"
									mr={[0, 0, '8px']}
									bgColor={theme.colors.gray[500]}
									borderRadius="base"
									borderBottom={`2px solid ${
										!(hazardStatus.completed || hazardStatus.pending) ? 'red' : 'transparent'
									}`}
								>
									<Switch.Root
										colorPalette="blue"
										size="lg"
										checked={hazardStatus.pending}
										onCheckedChange={() =>
											dispatch(
												updateHazardStatus({
													completed: false,
													pending: !hazardStatus.pending
												})
											)
										}
										disabled={Boolean(submission)}
										pointerEvents={submission ? 'none' : 'auto'}
										_hover={{ cursor: submission ? 'default' : 'pointer' }}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Pending
									</Text>
								</HStack>
							</Flex>
							<Flex flexDirection="row" alignItems="center" justifyContent="space-between">
								<Box
									w="100%"
									p="8px"
									m="8px"
									mx={[0, 0, '8px']}
									bgColor={theme.colors.gray[500]}
									borderRadius="base"
								>
									<Text fontSize={16} as="label" pb="4px">
										Comment
									</Text>
									<Input
										fontSize={16}
										disabled={Boolean(submission)}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
										bgColor={theme.colors.white}
										borderRadius="base"
										value={hazardStatusComment}
										onChange={({ target }) =>
											dispatch(
												updateTextField({
													key: 'hazard_status_comment',
													value: target.value
												})
											)
										}
									/>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={3} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Near Misses
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex
								flexDirection="row"
								alignItems="stretch"
								justifyContent="space-between"
								flexWrap={['wrap', 'wrap', 'nowrap']}
							>
								{Object.keys(nearMisses).map((key, index) => (
									<HStack
										key={`${key}_${index}`}
										w="100%"
										p="8px"
										m="8px"
										mx={[0, 0, '8px']}
										bgColor={theme.colors.gray[500]}
										borderRadius="base"
									>
										<Switch.Root
											colorPalette="blue"
											size="lg"
											checked={nearMisses[key].value}
											onChange={() =>
												dispatch(updateNearMisses({ key, value: !nearMisses[key].value }))
											}
											disabled={Boolean(submission)}
											pointerEvents={submission ? 'none' : 'auto'}
											_hover={{ cursor: submission ? 'default' : 'pointer' }}
										>
											<Switch.HiddenInput />
											<Switch.Control />
										</Switch.Root>
										<Text fontSize={16} as="label" pl="4px">
											{nearMisses[key].title}
										</Text>
									</HStack>
								))}
							</Flex>
							<TextField
								disabled={Boolean(submission)}
								title="Near Miss Description"
								requirement={!nearMissDescriptionReqMet}
								value={nearMissDescription}
								update={value => dispatch(updateTextField({ key: 'near_miss_description', value }))}
							/>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
			</Accordion.Root>
			<Flex
				flexDirection="row"
				alignItems="stretch"
				justifyContent="space-between"
				flexWrap={['wrap', 'wrap', 'nowrap']}
			>
				<Box w="100%" p="8px" m="8px" mx={[0, 0, '8px']} bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text fontSize={16} as="label" pb="4px">
						Project / Location
					</Text>
					<Field.Root invalid={!projectLocation}>
						<Autocomplete
							disabled={Boolean(submission)}
							_disabled={{
								opacity: 1,
								bgColor: theme.colors.gray[400],
								color: theme.colors.black
							}}
							dataList={projectLocationList}
							fieldName="project"
							value={projectLocation}
							onChange={value => dispatch(updateTextField({ key: 'project_location', value }))}
							onSelected={value => dispatch(updateTextField({ key: 'project_location', value }))}
						/>
					</Field.Root>
				</Box>
				<Box w="100%" p="8px" m="8px" mx={[0, 0, '8px']} bgColor={theme.colors.gray[500]} borderRadius="base">
					<Dialog.Root
						role="alertdialog"
						closeOnEscape={false}
						closeOnInteractOutside={false}
						onOpenChange={({ open }) => {
							if (!open) {
								setRemoveAutocompleteDialogOpen(false);
							}
						}}
						open={removeAutocompleteDialogOpen}
						placement="center"
					>
						<Portal>
							<Dialog.Backdrop />
							<Dialog.Positioner>
								<Dialog.Content opacity="1 !important">
									<Dialog.Header>
										<Dialog.Title fontSize="lg" fontWeight="bold">
											Could not find an employee match.
										</Dialog.Title>
									</Dialog.Header>
									<Dialog.Body>
										<p>
											Please check that the name is spelled correctly. We suggest you select a
											match from the autocomplete list below the input. The employee will be
											removed, please try adding again with a correct selection.
										</p>
									</Dialog.Body>
									<Dialog.Footer>
										<Button
											colorPalette="red"
											ml={3}
											bg={theme.colors.red[500]}
											_hover={{ background: theme.colors.red[600] }}
											color={theme.colors.white}
											onClick={() => {
												setRemoveAutocompleteDialogOpen(false);
												dispatch(updateTextField({ key: 'observer_name', value: '' }));
											}}
											size="lg"
										>
											Remove Item
										</Button>
									</Dialog.Footer>
								</Dialog.Content>
							</Dialog.Positioner>
						</Portal>
					</Dialog.Root>
					<Text fontSize={16} as="label" pb="4px">
						Observer Name
					</Text>
					<Field.Root invalid={!anonymous && !observerName}>
						<Autocomplete
							disabled={(Boolean(submission) && !editMode) || anonymous}
							_disabled={{
								opacity: 1,
								bgColor: theme.colors.gray[400],
								color: theme.colors.black
							}}
							onBlur={() =>
								setRemoveAutocompleteDialogOpen(observerName && !employeeCrewMembers.get(observerName))
							}
							dataList={window.employeeList}
							fieldName="name"
							value={observerName}
							onChange={value => {
								dispatch(updateTextField({ key: 'observer_name', value }));

								if (anonymous) {
									setAnonymous(false);
								}
							}}
							onSelected={value => dispatch(updateTextField({ key: 'observer_name', value }))}
						/>
					</Field.Root>
				</Box>
				<HStack
					p="8px"
					m="8px"
					mx={[0, 0, '8px']}
					w={['100%', '100%', 'auto']}
					bgColor={theme.colors.gray[500]}
					borderRadius="base"
				>
					<Switch.Root
						colorPalette="blue"
						disabled={Boolean(submission) && !editMode}
						size="lg"
						onChange={() => {
							setAnonymous(prevState => !prevState);

							if (observerName) {
								dispatch(updateTextField({ key: 'observer_name', value: '' }));
							}
						}}
						checked={anonymous}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						Submit Anonymously
					</Text>
				</HStack>
				<Box
					position="relative"
					w="100%"
					p="8px"
					m="8px"
					mx={[0, 0, '8px']}
					bgColor={theme.colors.gray[500]}
					borderRadius="base"
				>
					<Text fontSize={16} as="label" pb="4px">
						Date
					</Text>
					<Box
						_hover={{ cursor: submission ? 'default' : 'pointer' }}
						pointerEvents={submission ? 'none' : 'auto'}
					>
						<DatePicker
							disabled={Boolean(submission)}
							selected={date.date}
							onChange={date => dispatch(updateDate({ date: moment(date).valueOf(), manual: true }))}
							customInput={<Input fontSize={16} bgColor={theme.colors.white} borderRadius="base" />}
						/>
					</Box>
				</Box>
			</Flex>
		</Box>
	);
};
